<template>
  <section :class="$style.events">
    <h2 class="space-0-above">
      {{
        upcomingEvents.items && upcomingEvents.items.length && upcomingEvents.title
          ? upcomingEvents.title
          : 'Upcoming Events'
      }}
    </h2>
    <div v-if="events && events.length" :class="$style.grid">
      <div
        v-for="(event, key) in events"
        :key="key"
        :class="[
          $style.event,
          {
            [$style.oneUp]: events.length === 1,
            [$style.twoUp]: events.length === 2,
            [$style.threeUp]: events.length === 3,
          },
        ]"
      >
        <router-link
          :ga4-event="
            JSON.stringify({
              click_type: 'image',
              component: `${parent}`,
              content_group: 'module',
              gtm_tag: 'linked_image',
              file_name: `${event.featuredImage.node.sourceUrl.split('/').pop()}`,
              link_url: `${event.eventSlug}`,
            })
          "
          :to="{
            name: 'CalendarEvent',
            params: {
              date: event.start.isoDate,
              slug: event.eventSlug,
            },
          }"
        >
          <picture class="aspect-container">
            <img
              :alt="
                event.specificImage ? event.specificImage.altText : event.featuredImage.node.altText
              "
              :src="
                event.specificImage
                  ? event.specificImage.sourceUrl
                  : event.featuredImage.node.sourceUrl
              "
              :srcset="event.specificImage ? event.specificImage.imageSrcset : event.imageSrcset"
              class="aspect-image"
              loading="lazy"
              :sizes="get_sizes(events.length, key)"
            />
          </picture>
        </router-link>
        <section>
          <header>
            <h3 v-if="event.title" class="space-12-16-above">
              <router-link
                :ga4-event="
                  JSON.stringify({
                    click_type: 'link',
                    component: `${parent}`,
                    content_group: 'module',
                    gtm_tag: 'link',
                    file_name: `${event.title}`,
                    link_url: `${event.eventSlug}`,
                  })
                "
                :to="{
                  name: 'CalendarEvent',
                  params: {
                    date: event.start.isoDate,
                    slug: event.eventSlug,
                  },
                }"
                v-html="event.title"
              ></router-link>
            </h3>
          </header>
          <footer>
            <p v-if="event.venue.toLowerCase() === 'online'" v-html="`${event.venue}:&nbsp;`"></p>
            <p v-if="event.repeatDescription">{{ event.repeatDescription }}</p>
            <time
              v-if="!event.repeatDescription"
              class="body-1"
              v-html="
                !event.hideStartTime
                  ? `${formatDate(event.start, true)}, ${formatTimeSpan(event)}`
                  : `${formatDate(event.start, true)}`
              "
            ></time>
          </footer>
        </section>
      </div>
    </div>
    <div v-else>
      <p>
        There are currently no upcoming events. Sign up for our newsletter to be notified of new
        events and other news about the Guggenheim.
      </p>
    </div>
    <PreparedAnchor
      v-if="events && events.length"
      class="more-link"
      :to="
        upcomingEvents.items && upcomingEvents.items.length && upcomingEvents.more_link
          ? upcomingEvents.more_link
          : '/calendar'
      "
    >
      {{
        upcomingEvents.items && upcomingEvents.items.length && upcomingEvents.more_link_text
          ? upcomingEvents.more_link_text
          : 'Explore the Calendar'
      }}
    </PreparedAnchor>
    <a v-else class="more-link" href="https://cloud.emails.guggenheim.org/newsletter">Subscribe</a>
  </section>
</template>

<script setup lang="ts">
import { AppConfig } from '@/types/global.interface';
import gql from 'graphql-tag';
import { inject, ref, watch } from 'vue';
import PreparedAnchor from '@/components/common/PreparedAnchor.vue';
import { upcomingEventsQuery } from '@/queries/UpcomingEventsQuery';
import { useDateFormat, useHandleError, useRouteParent } from '@/composables/Common.js';
import { useQuery } from 'villus';
import { UpcomingEvent } from '@/types/event.interface';

const props = defineProps<{
  upcomingEvents: {
    items: Array<UpcomingEvent>;
    more_link: string;
    more_link_text: string;
    title: string;
  };
}>();

const events = ref();
const limit = 4;
const shouldSkip = ref(true);
const total = ref();

const { formatDate, formatTimeSpan } = useDateFormat();
const { handleError } = useHandleError();
const { parent } = useRouteParent();

const appConfig = inject<AppConfig>('appConfig');

const { data: queryData, error } = useQuery({
  query: gql`
    ${upcomingEventsQuery}
  `,
  skip: shouldSkip,
  variables: {
    first: limit,
    today: appConfig.today.isoDate,
  },
});

if (props.upcomingEvents.items && props.upcomingEvents.items.length) {
  events.value = props.upcomingEvents.items;
}

if (!props.upcomingEvents.items || props.upcomingEvents.items.length < 1) {
  shouldSkip.value = false;
}

watch(queryData, (data) => {
  if (data && Object.keys(data).length) {
    events.value = data.eventOccurrences.nodes;
    total.value = data.eventOccurrences.pageInfo.totalEventOccurrences;
  }

  if (!data) {
    handleError('UpcomingEvents', error);
  }
});

function get_sizes(count, key) {
  if (count % 2 != 0 && key == 0) {
    return '(min-width: 960px) 1280px, 100vw';
  }

  return '(min-width: 960px) 620px, 100vw';
}
</script>

<style lang="scss" module>
.event {
  @include space-48-64-above;
}

.events {
  &::before {
    @include line;

    & {
      margin-top: -2rem;
    }
  }

  :global(.more-link) {
    @include space-48-64-above;
  }

  footer {
    @include space-8-10-above;

    > * {
      display: inline-block;
      margin-top: 0;
    }
  }

  h3 {
    a {
      text-decoration: none;
    }
  }
}

@media (min-width: $break-point-small) {
  .event {
    grid-column: span 6;

    &.oneUp {
      grid-column: span 12;
    }
    &.threeUp {
      margin-top: 2rem;

      &:first-of-type {
        grid-column: span 12;
      }
    }
  }

  .grid {
    @include grid;
  }
}
</style>
